import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  ViewHomeLogItem, SituAnalyticsWebApiClient, ListViewHomeLogItemsRequest,
  CreateViewHomeLogItemsCsvReportRequest, IsReportReadyRequest, CreateLegacyMonthlyCsvReportRequest, IsReportReadyResponse
} from 'situanalytics-webapi-client';
import { PageEvent } from '@angular/material/paginator';
import { takeWhile, mergeMap } from 'rxjs/operators';
import { interval, Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { DownloadReportDialogComponent } from '../../../components/download-report-dialog/download-report-dialog.component';
import { AuthService, BreadcrumbService, ConfigService, StateService } from 'src/app/services';
import { Company } from 'identity-webapi-client';

@Component({
  selector: 'app-view-home-analytics',
  templateUrl: './view-home-analytics.component.html',
  styleUrls: ['./view-home-analytics.component.less'],
})
export class ViewHomeAnalyticsComponent implements OnInit, OnDestroy {

  private _loading = false;
  public get loading(): boolean {
    return this._loading;
  }

  public set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  selectedCompany: Company | undefined;
  pageNumber = 1;
  pageSize = 25;
  totalItems = 0;
  logItems: ViewHomeLogItem[] = [];
  reportId: string | undefined = undefined;
  isReportReady = false;
  reportErrorCount = 0;
  reportPollCount = 0;
  showLegacy = false;
  minLegacyDate = new Date(2021, 7, 30);
  maxLegacyDate = new Date();
  legacyDate = new Date();

  displayedColumns: string[] = [
    'dateStart',
    //'dateEnd',
    'clientIp',
    'type',
    'configuration',
    'room',
    //'renderIp',
    //'wait',
    //'total',
    //'result',
    'view'
  ];

  search: string = '';
  dateFrom: Date;
  dateTo: Date;
  clientIp: string = '';
  type = '';
  size: number | undefined = undefined;
  home: string = '';
  room: string = '';
  masterFile: string = '';
  themes: string = '';
  options: string = '';
  wait: number | undefined = undefined;
  total: number | undefined = undefined;
  result = '';

  pollForReportStatusSub: Subscription | null = null;
  private _subscriptions: Subscription[] = [];

  constructor(
    private situAnalyticsCLient: SituAnalyticsWebApiClient,
    public dialog: MatDialog,
    private breadcrumbService: BreadcrumbService,
    private stateService: StateService,
    private configService: ConfigService) {
    this.situLinkBaseUrl = this.configService.config.situLinkBaseUrl;
    this.situPortalBaseUrl = this.configService.config.situPortalBaseUrl;
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);

    this.dateFrom = yesterday;
    this.dateTo = now;
    this.legacyDate = new Date();
    this.legacyDate.setMonth(this.legacyDate.getMonth() - 1);
  }

  ngOnInit() {
    this._subscriptions.push(this.stateService.state$.subscribe((state) => {
      let company = state.company as Company ?? undefined
      if (this.selectedCompany?.id !== company?.id) {
        this.selectedCompany = state.company as Company ?? undefined;
        this.loadData();
        this.showLegacy = company?.code?.toLowerCase() == 'burbank';
      }
    }));
  }

  ngOnDestroy(): void {
    if (this._subscriptions) {
      for (let sub of this._subscriptions) {
        sub.unsubscribe();
      }
      this._subscriptions = [];
    }
  }

  situLinkBaseUrl: string | undefined;
  situPortalBaseUrl: string | undefined;
  openSituLinkInNewWindow(event: MouseEvent, artifactId: string, isFacade: boolean = false) {
    event.stopPropagation();
    const url = this.getSituLinkUrl(artifactId, isFacade);
    window.open(url, '_blank');
  }

  getSituLinkUrl(artifactId: string, isFacade: boolean = false): string {
    if (!artifactId) {
      return "";
    }

    const baseUrl = this.situLinkBaseUrl 
        ? `https://${isFacade ? 'facade' : 'view'}.${this.situLinkBaseUrl}`
        : this.situPortalBaseUrl!;
    
    const url = new URL(baseUrl);
    url.pathname = artifactId;

    return url.toString();
  }

  onPageChange(evt: PageEvent) {
    this.pageSize = evt.pageSize;
    this.pageNumber = evt.pageIndex + 1;
    this.loadData();
  }

  loadData() {
    this.loading = true;

    this.dateFrom.setHours(0, 0, 0, 0);
    this.dateTo.setHours(23, 59, 59, 999);

    const dateFrom = this.dateFrom;
    const dateTo = this.dateTo;

    const request = new ListViewHomeLogItemsRequest();
    request.pageNumber = this.pageNumber;
    request.pageSize = this.pageSize;
    request.dateFrom = dateFrom;
    request.dateTo = dateTo;
    request.search = this.search;
    request.clientIp = this.clientIp;
    request.type = this.type;
    request.home = this.home;
    request.room = this.room;
    request.facade = this.masterFile;
    (request as any).masterFile = this.masterFile;
    request.theme = this.themes;
    request.options = this.options;
    request.size = this.size;
    request.waitTimeInSeconds = this.wait;
    request.totalTimeInSeconds = this.total;
    request.result = this.result;
    request.ownerId = this.selectedCompany?.id;

    this.situAnalyticsCLient.listViewHomeLogItems(request)
      .then(response => {
        this.logItems = response.items ?? [];
        this.totalItems = response.totalItems ?? 0;

        this.loading = false;
      }, error => {
        this.loading = false;
      });
  }

  downloadCsv() {
    this.clearLastReport();

    this.dateFrom.setHours(0, 0, 0, 0);
    this.dateTo.setHours(23, 59, 59, 999);

    const dateFrom = this.dateFrom;
    const dateTo = this.dateTo;

    this.loading = true;

    const request = new CreateViewHomeLogItemsCsvReportRequest({
      dateFrom,
      dateTo,
      search: this.search,
      clientIp: this.clientIp,
      type: this.type,
      home: this.home,
      room: this.room,
      facade: this.masterFile,
      theme: this.themes,
      options: this.options,
      size: this.size,
      waitTimeInSeconds: this.wait,
      totalTimeInSeconds: this.total,
      result: this.result,
      ownerId: this.selectedCompany?.id
    });

    (request as any).masterFile = this.masterFile;

    this.situAnalyticsCLient.createViewHomeLogItemsCsvReport(request)
      .then(response => {
        this.reportId = response.reportId;
        this.pollForReportStatus();
      }, error => {
        this.loading = false;
      });
  }

  downloadLegacyCsv() {
    this.clearLastReport();
    this.loading = true;

    const request = new CreateLegacyMonthlyCsvReportRequest({
      month: this.legacyDate.getMonth() + 1, // month is zero based, but the api is not
      year: this.legacyDate.getFullYear(),
      ownerId: this.selectedCompany?.id
    });

    this.situAnalyticsCLient.createLegacyMonthlyCsvReport(request)
      .then(response => {
        this.reportId = response.reportId;
        this.pollForReportStatus();
      }, error => {
        this.loading = false;
      });
  }

  pollForReportStatus() {
    const pollDelayMilliseconds = 2000;
    const maxPolls = (60 * 60 * 1000) / pollDelayMilliseconds; // 1 hour max;

    this.pollForReportStatusSub = interval(pollDelayMilliseconds)
      .pipe(
        mergeMap(() => this.situAnalyticsCLient.isReportReady({ id: this.reportId } as IsReportReadyRequest)
          .then((response) => {
            if (response.isReady === true) {
              this.isReportReady = true;
              this.loading = false;
              this.openDownloadReportDialog();
            }

            this.reportPollCount++;
            if (this.reportPollCount >= maxPolls) {
              // show timeout error
              this.loading = false;
            }

            return response;
          }, (e) => {
            if (this.reportErrorCount > 1) {
              this.loading = false;
              throw e;
            }

            this.reportErrorCount++;

            let response = new IsReportReadyResponse();
            response.isReady = false;

            return response;
          })),
        takeWhile(response => response.isReady === false && this.reportPollCount < maxPolls)
      )
      .subscribe();
  }

  openDownloadReportDialog() {
    this.dialog.open(DownloadReportDialogComponent, {
      data: {
        reportId: this.reportId
      }
    });
  }

  clearLastReport() {
    if (this.pollForReportStatusSub) {
      this.pollForReportStatusSub.unsubscribe();
    }

    this.isReportReady = false;
    this.reportId = '';
    this.reportErrorCount = 0;
    this.isReportReady = false;
  }
}
